/**
 * LoadingSpinner component
 * Reusable spinner for buttons, inline loading, and page loading
 */

export default function LoadingSpinner({
  size = 'md',
  color = 'primary',
  className = '',
  label = 'Loading...',
  showLabel = false,
  fullPage = false,
}) {
  const sizeClasses = {
    xs: 'w-3 h-3 border',
    sm: 'w-4 h-4 border-2',
    md: 'w-6 h-6 border-2',
    lg: 'w-8 h-8 border-2',
    xl: 'w-12 h-12 border-3',
  };

  const colorClasses = {
    primary: 'border-primary-200 border-t-primary-600',
    white: 'border-white/30 border-t-white',
    gray: 'border-gray-200 border-t-gray-600',
    current: 'border-current/30 border-t-current',
  };

  const spinner = (
    <div
      className={`${sizeClasses[size]} ${colorClasses[color]} rounded-full animate-spin ${className}`}
      role="status"
      aria-label={label}
    />
  );

  if (fullPage) {
    return (
      <div className="fixed inset-0 bg-white/80 backdrop-blur-sm flex flex-col items-center justify-center z-50">
        <div className={`${sizeClasses.xl} ${colorClasses.primary} rounded-full animate-spin`} />
        {showLabel && (
          <p className="mt-4 text-gray-600 font-medium">{label}</p>
        )}
      </div>
    );
  }

  if (showLabel) {
    return (
      <div className="flex items-center gap-2">
        {spinner}
        <span className="text-sm text-gray-600">{label}</span>
      </div>
    );
  }

  return spinner;
}

// Button loading variant - replaces button content while loading
export function ButtonSpinner({ size = 'sm', className = '' }) {
  return (
    <LoadingSpinner size={size} color="current" className={className} />
  );
}

// Inline loading with text
export function InlineLoading({ text = 'Loading...', className = '' }) {
  return (
    <div className={`inline-flex items-center gap-2 text-gray-500 ${className}`}>
      <LoadingSpinner size="sm" color="gray" />
      <span className="text-sm">{text}</span>
    </div>
  );
}

// Page loading overlay
export function PageLoading({ message = 'Loading...' }) {
  return (
    <div className="min-h-[400px] flex flex-col items-center justify-center">
      <LoadingSpinner size="lg" color="primary" />
      <p className="mt-4 text-gray-500">{message}</p>
    </div>
  );
}

// Dot loading animation (alternative style)
export function DotsLoading({ className = '' }) {
  return (
    <div className={`flex items-center gap-1 ${className}`}>
      <div className="w-2 h-2 bg-primary-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
      <div className="w-2 h-2 bg-primary-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
      <div className="w-2 h-2 bg-primary-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
    </div>
  );
}
